import {LogoName} from '@benchling/nucleus-ui';
import * as PropTypes from 'prop-types';
import _ from 'underscore';

import {User} from 'constants/UserConstants';

export enum AccountType {
  BOX = 'BOX',
  DROPBOX = 'DROPBOX',
  EGNYTE = 'EGNYTE',
  GDRIVE = 'GDRIVE',
  HOSTED_S3 = 'HOSTED_S3',
  ONEDRIVE = 'ONEDRIVE',
}

export const ACCOUNT_TYPE_TO_LOGO: Record<AccountType, LogoName> = {
  [AccountType.BOX]: 'box-color',
  [AccountType.DROPBOX]: 'dropbox',
  [AccountType.EGNYTE]: 'egnyte-color',
  [AccountType.GDRIVE]: 'google-drive-color',
  [AccountType.ONEDRIVE]: 'one-drive',
  [AccountType.HOSTED_S3]: 'aws',
};

export interface TFIBase {
  id: string;
  name: string;
  accountType: AccountType;
}

const TFI_BASE_SCHEMA = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  // eslint-disable-next-line no-restricted-syntax
  accountType: PropTypes.oneOf(_.values(AccountType)).isRequired,
};

export interface TFIObject extends TFIBase {
  accountId: string;
  isFolder: boolean;
  parentId?: string | null;
  parent?: TFIObject | null;
  childIds?: Array<string>;
}

// eslint-disable-next-line no-restricted-syntax
const TFI_OBJECT_SCHEMA = _.extend({}, TFI_BASE_SCHEMA, {
  accountId: PropTypes.string.isRequired,
  isFolder: PropTypes.bool.isRequired,
});

export interface TFIFolder extends TFIObject {
  children: Array<TFIObject> | null;
}

const TFI_FOLDER_SHAPE = PropTypes.shape(
  // eslint-disable-next-line no-restricted-syntax
  _.extend({}, TFI_OBJECT_SCHEMA, {
    children: PropTypes.array,
  })
);

export interface TFIAccount extends TFIBase {
  user: User;
}

export interface TFIFileMeta {
  id: number;
  thirdpartyFileId: string;
  thirdpartyFileRev: string | null;
  tfiAccount: TFIAccount;
  syncStatus: string;
  syncStatusUpdated: string;
  lastSyncSuccess: string | null;
  lastSyncFailure: string | null;
  editURL: string | null;
}

export interface TFRevsByTFIMetaId {
  thirdpartyFileRevsByTFIMetaId: {
    [x: number]: string;
  };
}

const ACCOUNT_SHAPE = PropTypes.shape(TFI_BASE_SCHEMA);

export interface TFIFile extends TFIObject {
  modifiedAt: string;
  revisionId: string;
  fileSize: number;
}

export interface TFIFolderServerResponse {
  warning_message: string;
  results: Array<TFIFile | TFIFolder>;
}

export interface TFIFileMetaState {
  upstreamThirdpartyFileRev?: unknown;
  checkingForUpdates?: boolean;
}

const TFIConstants = {
  FILE: 'FILE',
  FOLDER: 'FOLDER',
  TFI_FILTER: 'TFI_FILTER',
  ACCOUNT_TYPE: AccountType,
  SYNC_STATUS: {NOT_STARTED: 'NOT_STARTED', IN_PROGRESS: 'IN_PROGRESS', COMPLETE: 'COMPLETE', ERROR: 'ERROR'},
  SYNCING_TIMEOUT_MESSAGE: 'Syncing the file timed out. Please try refreshing your page.',

  TFI_OBJECT_SHAPE: PropTypes.shape(TFI_OBJECT_SCHEMA),

  TFI_FILE_SHAPE: PropTypes.shape(
    // eslint-disable-next-line no-restricted-syntax
    _.extend({}, TFI_OBJECT_SCHEMA, {
      modifiedAt: PropTypes.string.isRequired,
      revisionId: PropTypes.string.isRequired,
      fileSize: PropTypes.number.isRequired,
    })
  ),

  TFI_FOLDER_SHAPE,
  ACCOUNT_SHAPE,
};

export default TFIConstants;
